/**
 * Physics Logger
 * Comprehensive logging for collision detection, physics bodies, and movement
 */

import { gameLogger } from '../../utils/GameLogger';

export interface CollisionEvent {
  type: 'player-platform' | 'player-enemy' | 'projectile-enemy' | 'player-teleporter' | 'velocity-change' | 'player-grounded' | 'player-airborne';
  playerId?: string;
  targetId?: string;
  position?: { x: number; y: number };
  velocity?: { x: number; y: number };
  timestamp: number;
}

export interface PhysicsBodyInfo {
  id: string;
  type: 'player' | 'platform' | 'enemy' | 'projectile';
  position: { x: number; y: number };
  size: { width: number; height: number };
  velocity?: { x: number; y: number };
  gravity?: { y: number };
  bounce?: number;
  immovable?: boolean;
}

export class PhysicsLogger {
  private static instance: PhysicsLogger;
  private collisionBuffer: CollisionEvent[] = [];
  private readonly bufferSize = 100;
  private lastCollisionLog = 0;
  private readonly collisionThrottleMs = 1000; // Throttle collision logging to 1 per second
  private quietMode = true; // Quiet mode - reduce console spam in development

  private constructor() {}

  public static getInstance(): PhysicsLogger {
    if (!PhysicsLogger.instance) {
      PhysicsLogger.instance = new PhysicsLogger();
    }
    return PhysicsLogger.instance;
  }

  /**
   * Log physics body creation
   */
  public logBodyCreation(body: PhysicsBodyInfo): void {
    if (!this.quietMode) {
      gameLogger.info('physics', '🎮 Physics Body Created', {
        id: body.id,
        type: body.type,
        position: body.position,
        size: body.size,
        velocity: body.velocity
      });
    }
  }

  /**
   * Set quiet mode to reduce console spam
   */
  public setQuietMode(quiet: boolean): void {
    this.quietMode = quiet;
  }

  /**
   * Log collision detection
   */
  public logCollision(event: CollisionEvent): void {
    const now = Date.now();
    
    // Throttle collision logging to prevent spam when stuck
    if (now - this.lastCollisionLog < this.collisionThrottleMs) {
      return;
    }
    this.lastCollisionLog = now;
    
    this.collisionBuffer.push(event);
    
    // Keep buffer at manageable size
    if (this.collisionBuffer.length > this.bufferSize) {
      this.collisionBuffer.shift();
    }

    if (!this.quietMode) {
      gameLogger.info('physics', '💥 Collision Detected', {
        type: event.type,
        playerId: event.playerId,
        targetId: event.targetId,
        position: event.position,
        timestamp: event.timestamp
      });
    }

    // Special handling for critical collisions
    if (event.type === 'player-platform' && event.position) {
      this.logPlatformCollision(event);
    }
  }

  /**
   * Log platform collision details
   */
  private logPlatformCollision(event: CollisionEvent): void {
    if (!this.quietMode) {
      gameLogger.debug('physics', '🦶 Platform Collision', {
        playerId: event.playerId,
        position: event.position,
        velocity: event.velocity
      });
    }
  }

  /**
   * Log physics update loop
   */
  public logPhysicsUpdate(objectCount: number, deltaTime: number): void {
    if (!this.quietMode) {
      gameLogger.debug('physics', '🔄 Physics Update', {
        objects: objectCount,
        deltaTime,
        fps: 1000 / deltaTime
      });
    }
  }

  /**
   * Log physics body removal
   */
  public logBodyRemoval(id: string, type: string): void {
    gameLogger.info('physics', '🗑️ Physics Body Removed', {
      id,
      type
    });
  }

  /**
   * Log velocity change
   */
  public logVelocityChange(id: string, velocity: { x: number; y: number }): void {
    gameLogger.debug('physics', '⚡ Velocity Changed', {
      id,
      velocity
    });
  }

  /**
   * Log gravity effect
   */
  public logGravityEffect(id: string, gravity: { y: number }): void {
    gameLogger.debug('physics', '🌍 Gravity Applied', {
      id,
      gravity
    });
  }

  /**
   * Get recent collisions
   */
  public getRecentCollisions(count: number = 10): CollisionEvent[] {
    return this.collisionBuffer.slice(-count);
  }

  /**
   * Clear collision buffer
   */
  public clearCollisionBuffer(): void {
    this.collisionBuffer = [];
    gameLogger.info('physics', '🧹 Collision buffer cleared', {});
  }
}

// Convenience functions for quick logging
export const logPhysicsBody = (body: PhysicsBodyInfo) => {
  PhysicsLogger.getInstance().logBodyCreation(body);
};

export const logCollision = (event: CollisionEvent) => {
  PhysicsLogger.getInstance().logCollision(event);
};

export const logPhysicsUpdate = (objectCount: number, deltaTime: number) => {
  PhysicsLogger.getInstance().logPhysicsUpdate(objectCount, deltaTime);
};